import { hasAllPermissions, hasAnyPermission } from '@bookora/shared';
import { anchorProblem, findAnchor } from '@bookora/ui';

import { useSessionStore } from '@/auth/token-store';

import {
  textOf,
  type Requirement,
  type StepAnchorId,
  type StepContext,
  type TourDef,
  type TourStepDef,
} from './tours/types';
import type { TourFeature } from './tours/types';

/* -------------------------------------------------------------------------- */
/* The plan features a step may need                                          */
/* -------------------------------------------------------------------------- */

type TourFeatures = Readonly<Record<TourFeature, boolean>>;

let known: { clinicId: string | null; features: TourFeatures } | null = null;

/**
 * What plan access last ANSWERED for the clinic on screen, recorded by the
 * features resolver (`guidance/features.ts` → `useTourFeatures`), which every
 * caller of `resolveSteps` renders first. The callers resolve steps without
 * threading a features record through, so the record lives here; it is keyed
 * by clinic, so a switch never lets one clinic's plan word another's tour.
 */
export function rememberTourFeatures(clinicId: string | null, features: TourFeatures): void {
  if (known?.clinicId === clinicId && known.features === features) return;
  known = { clinicId, features };
}

/** Tests only: back to «not known». */
export function forgetTourFeatures(): void {
  known = null;
}

/** The recorded features of the clinic on screen, or `null` while unknown. */
export function knownTourFeatures(): TourFeatures | null {
  if (!known || known.clinicId !== useSessionStore.getState().activeClinicId) return null;
  return known.features;
}

/**
 * Whether plan access has said `feature` is on — `true`, `false`, or `null`
 * while nothing is known. For a step BODY that words itself by plan (the
 * inventory tour's batch and expiry sentences): unknown keeps the words the
 * step always had, so a body never contradicts a screen still drawn open.
 */
export function tourFeatureOn(feature: TourFeature): boolean | null {
  const features = knownTourFeatures();
  return features ? features[feature] : null;
}

/** A step as the runner shows it: copy evaluated, anchor picked for the viewport. */
export interface ResolvedStep {
  id: string;
  anchor: StepAnchorId;
  title: string;
  body: string;
  placement: TourStepDef['placement'];
  spotlightPadding?: number;
}

export function meetsRequirement(
  req: Requirement | undefined,
  permissions: readonly string[],
): boolean {
  if (!req) return true;
  if (req.all && req.all.length > 0 && !hasAllPermissions(permissions, req.all)) return false;
  if (req.any && req.any.length > 0 && !hasAnyPermission(permissions, req.any)) return false;
  return true;
}

function anchorFor(step: TourStepDef, ctx: StepContext): StepAnchorId | null {
  if (typeof step.anchor === 'string') return step.anchor;
  return step.anchor[ctx.viewport];
}

/**
 * Pure: which steps this person could see, in order, before any DOM check.
 *
 *  1. permission (`requires`), plan feature (`requiresFeature`), persona and
 *     `when` filters;
 *  2. the anchor for the viewport (a step without one on this viewport is gone);
 *  3. the first eligible step of each `group`;
 *  4. at most `maxSteps` (`trimToMax`), unless `trim: false`.
 *
 * Whether the anchors exist on the page is the runner's question — and the
 * runner resolves with `trim: false` and trims only the steps whose anchors it
 * FOUND (critic C10): trimming first cut a step that had an anchor for one
 * that had none (the patient file's financial step, behind an empty alerts
 * strip), and «≤ 5 steps» means steps SHOWN.
 *
 * `requiresFeature` (owner D6): a step about a plan-locked tab is dropped,
 * like a step the viewer lacks the permission for. `opts.features` — the
 * fail-closed record from `useTourFeatures()` — decides when given: a feature
 * not known to be on drops the step. Without it the filter uses what plan
 * access last answered for this clinic (`knownTourFeatures`); before any
 * answer it filters nothing — the lock UI is still drawing every tab open
 * then (it fails open), and a step describes the screen as drawn. Whole-tour
 * gates (`tourAllowed`) always take the fail-closed record.
 */
export function resolveSteps(
  tour: TourDef,
  ctx: StepContext,
  opts: { trim?: boolean; features?: Readonly<Partial<Record<TourFeature, boolean>>> } = {},
): ResolvedStep[] {
  const features = opts.features ?? knownTourFeatures();
  const seenGroups = new Set<string>();
  const eligible: ResolvedStep[] = [];
  for (const step of tour.steps) {
    if (!meetsRequirement(step.requires, ctx.permissions)) continue;
    if (step.requiresFeature && features && features[step.requiresFeature] !== true) continue;
    if (step.personas && !step.personas.includes(ctx.persona)) continue;
    if (step.when && !step.when(ctx)) continue;
    const anchor = anchorFor(step, ctx);
    if (!anchor) continue;
    if (step.group) {
      if (seenGroups.has(step.group)) continue;
      seenGroups.add(step.group);
    }
    eligible.push({
      id: step.id,
      anchor,
      title: textOf(step.title, ctx),
      body: textOf(step.body, ctx),
      placement: step.placement,
      spotlightPadding: step.spotlightPadding,
    });
  }
  return opts.trim === false ? eligible : trimToMax(tour, eligible);
}

/**
 * At most `tour.maxSteps` of `steps`, keeping `priority` order when there are
 * more, in their original order. `keep` (a deep link's start step) survives
 * the cut whatever its priority. Generic, so the runner can trim its live
 * `{ step, el }` pairs with the same rule.
 */
export function trimToMax<T extends { id: string } | { step: { id: string } }>(
  tour: Pick<TourDef, 'maxSteps' | 'priority'>,
  steps: readonly T[],
  keep?: string,
): T[] {
  if (steps.length <= tour.maxSteps) return [...steps];
  const idOf = (s: T): string => ('step' in s ? s.step.id : s.id);
  const priority = tour.priority ?? steps.map(idOf);
  const rank = (id: string): number => {
    if (keep !== undefined && id === keep) return -1;
    const i = priority.indexOf(id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const kept = new Set(
    [...steps]
      .sort((a, b) => rank(idOf(a)) - rank(idOf(b)))
      .slice(0, tour.maxSteps)
      .map(idOf),
  );
  return steps.filter((s) => kept.has(idOf(s)));
}

/** Whether the tour may exist at all for this person (permissions and features; not anchors). */
export function tourAllowed(
  tour: TourDef,
  permissions: readonly string[],
  features: Readonly<Partial<Record<TourFeature, boolean>>>,
): boolean {
  if (!meetsRequirement(tour.requires, permissions)) return false;
  if (tour.requiresFeature && !features[tour.requiresFeature]) return false;
  return true;
}

/**
 * How many steps the runner would SHOW right now: resolved steps with an
 * anchor on the page (a scroll away at most), capped at `maxSteps` when given
 * — the same count the runner arrives at after `trimToMax`.
 */
export function usableStepCount(
  steps: readonly ResolvedStep[],
  root?: ParentNode,
  maxSteps?: number,
): number {
  const live = steps.filter((step) => {
    const el = findAnchor(step.anchor, root);
    return el !== null && anchorProblem(el, { viewport: false }) === null;
  }).length;
  return maxSteps === undefined ? live : Math.min(live, maxSteps);
}
